/* @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import { stringKeyToTuple } from 'js/lib/stringKeyTuple';

import { Button } from '@coursera/cds-core';

import type { CmlContent } from 'bundles/cml';
import { CMLUtils } from 'bundles/cml';
import { editReply, hideReplyEditor } from 'bundles/discussions/actions/DropdownActions';
import ForumsCmlEditor from 'bundles/discussions/components/ForumsCmlEditor';
import { DTD_NAME } from 'bundles/discussions/constants';
import DiscussionsCMLUtils, { getErrorMessageFromCode } from 'bundles/discussions/utils/DiscussionsCMLUtils';

import _t from 'i18n!nls/discussions';

import 'css!bundles/discussions/components/NewThreadForm/__styles__/NewThreadForm';

import type { ForumPostWithCreator } from '../../__providers__/ForumPostDataProvider/__types__';

type PropsFromCaller = {
  reply: ForumPostWithCreator;
  ariaLabel?: string;
  ariaDescribedBy?: string;
};

type PropsFromStore = {
  courseId: string;
};

type PropsToComponent = PropsFromCaller & PropsFromStore;

type State = {
  initialValue: string;
  cml: CmlContent;
  hasErrors: boolean;
  submitError: boolean;
  lengthError: boolean;
  submitEnabled: boolean;
  submitErrorMessage?: string;
};

class ReplyCMLEdit extends React.Component<PropsToComponent, State> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof ReplyCMLEdit.contextTypes>;

  declare editorElemRef: any;

  constructor(props: $TSFixMe, context: $TSFixMe) {
    super(props, context);
    const modelContent = props.reply.content.details;
    let cml = modelContent;

    // convert non-cml post into cml
    // can be removed if all posts are cml
    if (!modelContent || !modelContent.typeName) {
      const value = '<co-content><text>' + (modelContent || '') + '</text></co-content>';
      cml = CMLUtils.create(value, DTD_NAME);
    }

    // Use the new dtdId. Can be removed once all posts have the current dtdId
    cml.definition.dtdId = DTD_NAME;

    this.state = {
      initialValue: cml.definition.value,
      cml,
      hasErrors: false,
      submitError: false,
      lengthError: false,
      submitEnabled: false,
    };

    this.editorElemRef = null;
  }

  handleSubmit = () => {
    if (this.state.hasErrors) {
      return;
    }

    this.setState({
      submitEnabled: false,
      submitError: false,
      submitErrorMessage: undefined,
    });

    this.context.executeAction(editReply, {
      reply: this.props.reply,
      content: this.state.cml,
      handleFailure: this.onSubmitError,
      onSuccess: this.onSubmitSuccess,
    });
  };

  handleCancel = () => {
    this.hideEdit();
  };

  handleInputChange = (cml: $TSFixMe) => {
    this.setState({
      cml,
      hasErrors: !DiscussionsCMLUtils.validateLength(cml),
      submitEnabled: cml.definition.value !== this.state.initialValue,
    });
  };

  onSubmitError = (error: $TSFixMe) => {
    this.setState({
      submitError: true,
      submitEnabled: true,
      submitErrorMessage: getErrorMessageFromCode(error?.responseJSON?.errorCode),
    });
  };

  onSubmitSuccess = () => {};

  hideEdit() {
    this.context.executeAction(hideReplyEditor, { reply: this.props.reply });
  }

  render() {
    const { reply, courseId, ariaLabel, ariaDescribedBy } = this.props;
    const { cml, submitError, submitErrorMessage, submitEnabled, hasErrors } = this.state;
    const contentLengthWarning = DiscussionsCMLUtils.generateContentLengthWarning(cml);
    const questionIdParts = stringKeyToTuple(reply.questionId || '');
    const questionId = questionIdParts[questionIdParts.length - 1];
    const imageUploadOptions = DiscussionsCMLUtils.getImageUploadOptions(courseId, questionId);

    return (
      <div className="rc-ReplyCML" data-rc="ReplyCML">
        <div className="editor-container">
          <ForumsCmlEditor
            cml={cml}
            placeholder={_t('Reply')}
            imageUploadOptions={imageUploadOptions}
            ariaLabel={ariaLabel || _t('Edit Reply')}
            ariaDescribedBy={ariaDescribedBy}
            onChange={this.handleInputChange}
          />
        </div>
        <div className="horizontal-box align-items-right">
          <div className="flex-2">
            {contentLengthWarning}
            {submitError && <div className="c-error-text c-form-error-message">{submitErrorMessage}</div>}
          </div>
          <Button variant="primary" className="passive cancel" onClick={this.handleCancel}>
            {_t('Cancel')}
          </Button>
          <Button
            variant="secondary"
            className="secondary"
            onClick={this.handleSubmit}
            disabled={!submitEnabled || hasErrors}
          >
            {_t('Save')}
          </Button>
        </div>
      </div>
    );
  }
}

export default connectToStores<PropsToComponent, PropsFromCaller>(['CourseStore'], ({ CourseStore }) => {
  return {
    courseId: CourseStore.getCourseId(),
  };
})(ReplyCMLEdit);
